import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ViewChild
} from '@angular/core';
import { EntityCardsComponent, SimpleEntityListPerspective } from '@homeleite/common-components';
import { Actives, InterceptorConfig } from '@homeleite/arch';
import { finalize, map } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY } from '@homeleite/common-utils';
import { BreedersApiRepository } from '../../../../../../libs/domain/src/lib/breeders/breeders-api.repository';
import { BreederSummary } from '@homeleite/domain';

@Component({
    selector: 'homeleite-breeders',
    templateUrl: './breeders.component.html',
    styleUrls: ['./breeders.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BreedersComponent {
    @ViewChild('list') list: EntityCardsComponent;

    isSyncing = false;
    search: string = EMPTY;
    actives: Actives = Actives.TRUE;
    readonly Actives = Actives;
    busyId: string | null = null;
    perspective: SimpleEntityListPerspective = new SimpleEntityListPerspective();

    constructor(
        private changeDetector: ChangeDetectorRef,
        public repository: BreedersApiRepository,
        private snackBar: MatSnackBar
    ) {
        this.perspective.actives = Actives.TRUE;
    }

    setActives(value: Actives): void {
        if (this.actives === value) return;
        this.actives = value;
        const next = new SimpleEntityListPerspective();
        next.actives = value;
        this.perspective = next;
        this.changeDetector.markForCheck();
    }

    public toggleActive(entity: BreederSummary): void {
        if (!entity?.id || this.busyId) return;
        const turningOff = entity.isActive !== false;
        const name = this.displayName(entity);
        const question = turningOff
            ? `Inativar ${name}? Ele deixa de aparecer entre os ativos.`
            : `Ativar ${name} novamente?`;
        if (!confirm(question)) return;

        this.busyId = entity.id;
        this.changeDetector.markForCheck();
        const request = turningOff
            ? this.repository.delete(entity as any, false, { autoCatch: InterceptorConfig.NO_INTERCEPT }).pipe(map(() => true))
            : this.repository.activate(entity as any, { autoCatch: InterceptorConfig.NO_INTERCEPT }).pipe(map(() => true));
        request.pipe(finalize(() => {
            this.busyId = null;
            this.changeDetector.markForCheck();
        })).subscribe({
            next: () => {
                this.showSnackbarMessage(turningOff ? 'Reprodutor inativado.' : 'Reprodutor ativado.');
                this.list?.invalidate();
            },
            error: () => {
                this.showSnackbarMessage(
                    turningOff ? 'Não foi possível inativar o reprodutor.' : 'Não foi possível ativar o reprodutor.'
                );
            }
        });
    }

    public displayName(entity: BreederSummary): string {
        const name = (entity?.name || '').toString().trim();
        return name || 'Sem nome';
    }

    public getNickname(entity: BreederSummary): string {
        const nick = (entity?.nickName || '').toString().trim();
        return nick || '—';
    }

    public getRegister(entity: BreederSummary): string {
        const register = (entity?.register || '').trim();
        const central = (entity?.centralCode || '').trim();
        if (register && central) return `${register} · ${central}`;
        return register || central || 'Sem registro';
    }

    public getCompany(entity: BreederSummary): string {
        return entity?.company?.name || 'Não informada';
    }

    public getStatusLabel(entity: BreederSummary): string {
        return entity?.isActive ? 'Ativo' : 'Inativo';
    }

    public syncBreeders(): void {
        this.isSyncing = true;
        this.changeDetector.markForCheck();

        this.repository
            .syncBreeders(EMPTY)
            .pipe(
                finalize(() => {
                    this.isSyncing = false;
                    this.changeDetector.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.showSnackbarMessage('Sincronização finalizada com sucesso!');
                    this.list?.invalidate();
                    this.changeDetector.markForCheck();
                },
                error: (err) => {
                    console.error(err);
                    this.showSnackbarMessage('Erro ao sincronizar dados!');
                }
            });
    }

    public onSearchChange(searchValue: string) {
        this.perspective.filters = [];
        this.search = searchValue || EMPTY;
    }

    private showSnackbarMessage(message: string, duration = 6000) {
        this.snackBar.open(message, 'OK', {
            duration: duration,
            panelClass: ['custom-snackbar'],
            verticalPosition: 'bottom'
        });
    }
}
